import * as React from "react";
import { useState, useCallback } from "react";
import {
  makeStyles,
  shorthands,
  Button,
  Card,
  Text,
  Title2,
  Body1,
  Spinner,
  Badge,
} from "@fluentui/react-components";
import {
  ShieldLockRegular,
  ShieldCheckmarkRegular,
  BuildingMultipleRegular,
} from "@fluentui/react-icons";

// Define the exact colors from the image/requirement
const themeColors = {
  primaryGreen: "#0B5D5D",
  hoverGreen: "#094a4a",
  activeGreen: "#063030",
  bgGreenLight: "#E6F4F4",
  textDark: "#1F1F1F",
  textGrey: "#616161",
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#FFFFFF",
    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  // Navbar Styles
  navbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.padding("8px", "16px"),
    width: "100%",
    boxSizing: "border-box",
  },
  brandContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  brandIcon: {
    color: themeColors.primaryGreen,
    fontSize: "24px",
  },
  brandText: {
    fontWeight: 600,
    fontSize: "16px",
    color: themeColors.textDark,
  },
  secureBadge: {
    backgroundColor: themeColors.bgGreenLight,
    color: themeColors.primaryGreen,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    ...shorthands.padding("4px", "12px"),
    ...shorthands.borderRadius("16px"),
    fontWeight: 500,
    fontSize: "12px",
  },
  // Main Content Styles
  mainContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("10px"),
  },
  card: {
    width: "100%",
    maxWidth: "360px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    ...shorthands.padding("24px", "16px"),
    backgroundColor: "#FFFFFF",
    ...shorthands.borderRadius("6px"),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    ...shorthands.border("1px", "solid", "#E0E0E0"),
  },
  iconContainer: {
    width: "64px",
    height: "64px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  shieldIcon: {
    fontSize: "32px",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    color: themeColors.primaryGreen,
    marginBottom: "16px",
  },
  description: {
    fontSize: "14px",
    color: themeColors.textDark,
    marginBottom: "24px",
    lineHeight: "20px",
    textAlign: "center",
  },
  contactButton: {
    width: "100%",
    backgroundColor: themeColors.primaryGreen,
    color: "#FFFFFF",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("6px"),
    paddingTop: "8px",
    paddingBottom: "8px",
    fontWeight: 600,
    fontSize: "14px",
    "&:hover": {
      backgroundColor: themeColors.hoverGreen,
      color: "#FFFFFF",
    },
    "&:active": {
      backgroundColor: themeColors.activeGreen,
      color: "#FFFFFF",
    },
  },
  // Bottom Action Styles
  bottomContainer: {
    ...shorthands.padding("12px"),
    width: "100%",
    boxSizing: "border-box",
    marginTop: "24px", // Fixed spacing from content
  },
  refreshButton: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    color: themeColors.primaryGreen,
    ...shorthands.border("1px", "solid", themeColors.primaryGreen),
    ...shorthands.borderRadius("6px"), // 6px radius
    paddingTop: "10px",
    paddingBottom: "10px",
    fontWeight: 600,
    fontSize: "14px",
    "&:hover": {
      backgroundColor: themeColors.bgGreenLight,
      color: themeColors.primaryGreen,
    },
  },
});

interface ContactSupportPageProps {
  onRetry?: () => void;
  userEmail?: string;
}

const SUPPORT_EMAIL = "support@contravault.com";

const ContactSupportPage: React.FC<ContactSupportPageProps> = ({
  onRetry,
  userEmail
}) => {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);

  const handleContactSupport = useCallback(() => {
    setIsLoading(true);
    // Simulating action or mailto
    setTimeout(() => {
      const subject = encodeURIComponent("ContraVault Outlook Add-in Access Request");
      const body = encodeURIComponent(
        `Hi ContraVault Support,\n\nI need access to the ContraVault Outlook Add-in.\n\nUser Email: ${userEmail || 'Not provided'}\n\nPlease help me get access.\n\nThank you!`
      );
      window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`, '_blank');
      setIsLoading(false);
    }, 500);
  }, [userEmail]);

  return (
    <div className={styles.root}>
      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.card}>
          <div className={styles.iconContainer}>
            <img src="assets/secure.png" alt="Secure" style={{ width: '32px', height: '32px' }} />
          </div>

          <Text className={styles.title}>Access Required</Text>

          <Body1 className={styles.description}>
            You need to contact ContraVault AI to get access for Outlook Add-In.
          </Body1>

          <Button
            className={styles.contactButton}
            onClick={handleContactSupport}
            disabled={isLoading}
          >
            {isLoading ? <Spinner size="tiny" /> : "Contact ContraVault AI"}
          </Button>
        </div>
      </div>

      {/* Bottom Action */}
      <div className={styles.bottomContainer}>
        {onRetry && (
          <Button
            className={styles.refreshButton}
            onClick={onRetry}
          >
            Refresh Email
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContactSupportPage;